<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import CustomButton from '@/components/common/CustomButton.vue';
import { ButtonType } from '@/components/common';
import Select from 'primevue/select';
import InputOtp from 'primevue/inputotp';
import { LocalStorageUtils, routeConstants } from '@/utils';
import type { EntryUpdateReason } from '@/models';
import { useRouter } from 'vue-router';

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const reasonsList = [
  'I approve this document',
  'I have reviewed this document',
  'I am the owner of this document'
];

const router = useRouter();
const comment = ref<string>('');
const reason = ref<string>('');
const enteredPin = ref('');
const showWrongPinError = ref(false);

const onInputeChange = () => {};

const emits = defineEmits(['onClose', 'onSave']);

const closeDialog = async () => {
  clearForm();
  emits('onClose');
};

const saveReason = async () => {
  if (enteredPin.value !== LocalStorageUtils.getPart11Pin()) {
    showWrongPinError.value = true;
    return;
  }
  const reasonData: EntryUpdateReason = {
    reason: reason.value,
    comment: comment.value,
    type: 'update'
  };
  clearForm();
  emits('onSave', reasonData);
};

const enableSaveButton = computed(() => {
  return reason.value !== '' && enteredPin.value !== '';
});

const onPinChange = () => {
  showWrongPinError.value = false;
};

const clearForm = () => {
  reason.value = '';
  comment.value = '';
  enteredPin.value = '';
  showWrongPinError.value = false;
};

const resetPin = () => {
  router.push({ name: routeConstants.PART_11_PIN_SETUP.name });
};
</script>

<template>
  <Dialog :visible="visible"
    modal
    header="Reason for Signing"
    class="dialog"
    style="width: 500px"
    :closeOnEscape="true"
    :draggable="false"
    @update:visible="closeDialog">
    <div class="dialog-root">
      <div class="description">
        Please verify that the information below is correct and select a reason
        for this signing.
      </div>

      <div class="name-container">
        <div class="name-label">Signatory Name:</div>
        <div class="name">{{ LocalStorageUtils.getUserName() }}</div>
      </div>
      <div class="name-container">
        <div class="name-label">Signatory Email:</div>
        <div class="name">{{ LocalStorageUtils.getUserEmail() }}</div>
      </div>
      <Select v-model="reason"
        placeholder="Select a reason *"
        :options="reasonsList"
        class="reason-select"/>
      <Textarea v-model="comment"
        autoResize
        rows="2"
        :placeholder="'Comment (optional)'"
        @input="onInputeChange"
        class="text-area"/>
      <div class="auth-label">
        21 CFR Part 11 requires you to authenticate each change on this
        document *
      </div>
      <div class="pin-container">
        <InputOtp v-model="enteredPin" mask @change="onPinChange" />
        <div class="pin-error" v-if="showWrongPinError">Incorrect PIN!</div>
        <CustomButton text="Reset My PIN"
          class="reset-pin-button"
          :type="ButtonType.OUTLINED"
          @onClick="resetPin"/>
      </div>
      <div class="buttons">
        <CustomButton text="Cancel"
          :type="ButtonType.WITHOUT_OUTLINE"
          @onClick="closeDialog"/>
        <CustomButton text="Save"
          :type="ButtonType.POSITIVE"
          @onClick="saveReason"
          :disabled="!enableSaveButton"/>
      </div>
    </div>
  </Dialog>
</template>

<style lang="scss" scoped>
.description {
  font-size: 14px;
  margin-bottom: 16px;
}
.name-container {
  display: flex;

  .name-label {
    font-weight: 600;
    color: var(--text-color);
  }

  .name {
    margin-left: 8px;
  }
}
.reason-select {
  width: 100%;
  margin-top: 16px;
}
.text-area {
  width: 100%;
  font-size: 14px;
  min-height: 38px;
  margin-top: 16px;
}
.auth-label {
  margin-top: 16px;
  margin-bottom: 8px;
}
.pin-container {
  width: 100%;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;

  .pin-error {
    color: red;
    font-size: 14px;
    margin-top: 8px;
  }

  .reset-pin-button {
    margin-top: 16px;
  }
}
.buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}
</style>
